

























import {mapActions, mapGetters} from 'vuex';
import {AdvertTypeItems} from '@/store/modules/Advert/state';
import {queryFromCategory} from '@/store/modules/Category/getters';
import Vue from 'vue';
import {AdvertType} from '#/Advert';
import {CategoryAPI} from '#/Category';

interface ComponentData {
  saveSearch: boolean;
  searchName: string;
  searchText: string;
  selectedTypes: AdvertType[];
  openCategories: CategoryAPI[];
  selectedCategories: CategoryAPI[];
}

export default Vue.extend({
  name: 'DefaultSettings',

  components: {
    'type-selection': () => import('@/components/advert/creation/Type.vue'),
  },

  data: (): ComponentData => ({
    saveSearch: false,
    searchName: '',
    searchText: '',
    selectedTypes: [],
    openCategories: [],
    selectedCategories: [],
  }),

  computed: {
    ...mapGetters([
      'categories',
      'categoriesFromIdList',
      'categoryFromId',
      'profile',
      'saveSettings',
    ]),
  },

  watch: {
    saveSettings: function () {
      if (this.saveSettings) this.saveDefaultFilter();
    },
  },

  created() {
    this.loadCategories()
        .then(() => {
          this.updatePageTitle(this.$t('profile.defaultSettings.pageTitle'));
          this.updateAppBarContext({type: 'defaultSettings', status: true});

          if (this.profile.defaultFilter) {
            const {types, categories, parentCategories} = this.profile.defaultFilter;
            this.selectedTypes = types.map((id: number) => AdvertTypeItems[id]);

            // récupère les catégories de premier et second niveau
            const rootSelection: CategoryAPI[] = [];
            let midSelection: number[] = [...parentCategories];

            // Si toutes le catégories de second niveau sont cochées, coche la catégorie de premier niveau correspondante
            this.categories.forEach((cat: CategoryAPI) => {
              // renvoie un tableau avec les id de toutes les catégories de second niveau
              let childrenList = cat.children?.map(c => c.id);

              // Permet de cocher la catégorie parente si toutes les catégories enfantes sont cochées
              // si toutes catégories de second niveau d'une même catégorie de premier niveaux sont dans le tableau
              if (childrenList?.every((id: number) => midSelection.includes(id))) {
                // ...on ajoute la catégorie de premier niveau dans rootSelection
                rootSelection.push(cat);
                // et on supprime toutes les catégories de second niveau associées
                midSelection = midSelection.filter(id => !childrenList?.includes(id));
              }
            });

            // Récupère les catégories avec les id stockés dans le tableau
            const midSelectionCat: CategoryAPI[] = this.categoriesFromIdList(midSelection);

            // Récupère les catégories de 3ème niveau sélectionnées
            const bottomSelection = this.categoriesFromIdList(categories);

            // Fusionne les trois tableaux
            this.selectedCategories = bottomSelection.concat(rootSelection, midSelectionCat);

            // ouvre l'arbre pour que soit visible toutes les catégories sélectionnées
            const midOpen = this.getParents(bottomSelection);

            this.openCategories = this.getParents(midOpen.concat(midSelectionCat))
                                      .concat(midOpen);
          }
        });
  },

  methods: {
    ...mapActions([
      'loadCategories',
      'saveDefaultSettings',
      'updateAppBarContext',
      'updatePageTitle',
      'updateProfile',
    ]),
    /**
     * Calcule la liste des parents d'un tableau de catégories(non racine)
     */
    getParents(categories: CategoryAPI[]): CategoryAPI[] {
      return categories
          .reduce((parentList: number[], cat) => {
            if (cat.parentCategory && !parentList.includes(cat.parentCategory)) {
              parentList.push(cat.parentCategory);
            }

            return parentList;
          }, [])
          .map(parentId => this.categoryFromId(parentId));
    },
    saveDefaultFilter(): Promise<void> {
      return new Promise((resolve, reject) => {
        const defaultCategoriesFilter = this.selectedCategories
                                            .map(c => queryFromCategory(c))
                                            .reduce<{ categories: number[], parentCategories: number[] }>((acc,
                                                                                                           cur,
                                                ) => {
                                                  if (cur.parentCategories) {
                                                    acc.parentCategories = acc.parentCategories
                                                                              .concat(cur.parentCategories);
                                                  } else {
                                                    if (cur.categories) {
                                                      acc.categories.push(cur.categories);
                                                    }
                                                  }

                                                  return acc;
                                                }, {categories: [], parentCategories: []},
                                            );
        this.updateProfile({
          ...this.profile,
          defaultFilter: {
            types: this.selectedTypes.map(t => t.raw),
            ...defaultCategoriesFilter,
          },
        })
            .then(() => {
              this.saveDefaultSettings(false);
              this.$router.push({
                name: 'DefaultList',
              })
                  .then(() => {
                    resolve();
                  })
                  .catch(() => {
                    reject();
                  });
            });
      });
    },
    /**
     * Met à jour la selection de catégories, selon les règles suivantes :
     * La selection de catégories ne peut contenir une catégorie et une de ses parentes
     *
     * La selection d'une catégorie entraîne la suppression de
     *    - ses éventuelles catégories filles
     *    - ses éventuelles catégories parentes
     */
    onUpdateTreeView(itemsSelected: CategoryAPI[]): void {
      const lastSelection = itemsSelected[itemsSelected.length - 1];
      this.selectedCategories = itemsSelected;

      if (lastSelection) {
        const parent = lastSelection.parentCategory;
        // removes parent and grandparent of last selected category
        if (parent) {
          this.selectedCategories = this.selectedCategories.filter(cat => {
            return (cat.id !== parent) &&
                (
                    cat.children
                        ? !cat.children.map(c => c.id)
                              .includes(parent)
                        : true
                );
          });
        }
        // removes children and grandchildren of last selected category
        if (lastSelection.children) {
          const descendants = lastSelection.children.reduce<number[]>((acc, cur) => {
            return acc.concat(
                [cur.id],
                cur.children ? cur.children.map(cat => cat.id) : [],
            );
          }, []);
          this.selectedCategories = this.selectedCategories.filter(
              cat => !descendants.includes(cat.id),
          );
        }
      }
    },
  },
});
